/**
 * AuthProvider (ADR-007 overlay): session bootstrap via `GET /auth/me`,
 * login/logout, and 401 fan-out from the gateway client. Children only
 * render once a session exists; the login screen renders otherwise.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import type { Tenant, User } from "@llm-wiki/shared"
import {
  fetchMe,
  login as gatewayLogin,
  logout as gatewayLogout,
  onUnauthorized,
} from "./gateway"

export interface AuthState {
  status: "loading" | "unauthenticated" | "authenticated"
  user: User | null
  tenant: Tenant | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>")
  return ctx
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState["status"]>("loading")
  const [user, setUser] = useState<User | null>(null)
  const [tenant, setTenant] = useState<Tenant | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchMe()
      .then((me) => {
        if (cancelled) return
        if (me) {
          setUser(me.user)
          setTenant(me.tenant)
          setStatus("authenticated")
        } else {
          setStatus("unauthenticated")
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("unauthenticated")
      })
    const off = onUnauthorized(() => {
      if (!cancelled) {
        setUser(null)
        setTenant(null)
        setStatus("unauthenticated")
      }
    })
    return () => {
      cancelled = true
      off()
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await gatewayLogin(email, password)
    setUser(res.user)
    // /auth/me carries the tenant; refresh it after login.
    const me = await fetchMe()
    if (me) setTenant(me.tenant)
    setStatus("authenticated")
  }, [])

  const logout = useCallback(async () => {
    try {
      await gatewayLogout()
    } finally {
      setUser(null)
      setTenant(null)
      setStatus("unauthenticated")
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({ status, user, tenant, login, logout }),
    [status, user, tenant, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
